import { useState } from "react";
import { Link, useNavigate, useSearchParams, Navigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import BrandPanel from "../components/BrandPanel";
import LangToggle from "../components/LangToggle";
import { useLang } from "../context/LangContext";
import { Building2, Lock, ScrollText, Mail, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import LocationPicker from "../components/LocationPicker";

/**
 * Single-step registration.
 *
 * Flow:
 *   1. User fills: company name, email, country, city, password, ledger mode
 *   2. POST /auth/register → backend returns { setup_token } for TOTP enrolment
 *   3. Navigate to /login/totp/setup with the token (QR + first code + backup codes)
 */
export default function Register() {
    const { register } = useAuth();
    const navigate = useNavigate();
    const { lang, t, toggle } = useLang();
    const [searchParams] = useSearchParams();
    const requestedType = searchParams.get("type"); // "manager" | "individual"
    const needsTypePick = requestedType !== "manager" && requestedType !== "individual";
    const accountType = needsTypePick ? "individual" : requestedType;

    const [companyName, setCompanyName] = useState("");
    const [email, setEmail] = useState("");
    const [country, setCountry] = useState("");
    const [countryCode, setCountryCode] = useState("");
    const [city, setCity] = useState("");
    const [password, setPassword] = useState("");
    const [confirm, setConfirm] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    if (needsTypePick) return <Navigate to="/register/type" replace />;

    const submit = async (e) => {
        e?.preventDefault?.();
        setError("");
        if (!companyName.trim()) return setError(t.companyLabel);
        if (!email.trim()) return setError(t.emailRequired);
        if (!country || !city) return setError(t.locationRequired);
        if (password.length < 4) return setError(t.passwordTooShort);
        if (password !== confirm) return setError(t.passwordMismatch);

        setLoading(true);
        const res = await register({
            company_name: companyName.trim(),
            email: email.trim(),
            country,
            country_code: countryCode,
            city,
            password,
            account_type: accountType,
        });
        setLoading(false);
        if (!res.ok) {
            setError(res.error);
            toast.error(res.error);
            return;
        }
        if (res.requires_totp_setup) {
            navigate("/login/totp/setup", {
                state: { setup_token: res.setup_token, company_name: companyName, email: email.trim() },
            });
            return;
        }
        navigate("/", { replace: true });
    };

    const iconSide = lang === "ar" ? { right: "0.75rem" } : { left: "0.75rem" };
    const inputPad = lang === "ar" ? "h-11 pe-10 ps-3" : "h-11 ps-10 pe-3";

    return (
        <div className="relative min-h-screen w-full app-bg" dir={t.dir}>
            <LangToggle lang={lang} onToggle={toggle} t={t} />

            <div className="mx-auto grid min-h-screen max-w-7xl grid-cols-1 lg:grid-cols-2">
                <div className="relative hidden p-6 lg:block">
                    <BrandPanel lang={lang} strings={t} />
                </div>

                <div className="flex items-center justify-center px-4 py-8 sm:px-6 sm:py-12 lg:px-12">
                    <div className="w-full max-w-md fade-up">
                        <div className="mb-6 flex items-center gap-3 sm:mb-8">
                            <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-neutral-900 text-white sm:h-12 sm:w-12">
                                <ScrollText className="h-5 w-5 sm:h-6 sm:w-6" />
                            </div>
                            <div>
                                <h1 className="font-heading text-xl font-bold sm:text-2xl">{t.brand}</h1>
                                <p className="text-xs text-neutral-500 sm:text-sm">{t.registerAppTagline}</p>
                            </div>
                        </div>

                        <h2 className="font-heading text-2xl font-bold sm:text-3xl">{t.registerTitle}</h2>
                        <p className="mt-2 text-sm text-neutral-500">{t.registerSubtitle}</p>

                        <div className="mt-3 flex items-start gap-2 rounded-lg border border-emerald-200 bg-emerald-50/60 px-3 py-2 text-xs text-emerald-800">
                            <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0" />
                            <span>{t.registerTotpNote}</span>
                        </div>

                        <form onSubmit={submit} className="mt-6 space-y-4 sm:mt-8 sm:space-y-5" data-testid="register-form">
                            <div>
                                <Label className="mb-1.5 block text-sm font-medium">{t.companyLabel}</Label>
                                <div className="relative">
                                    <Building2 className="pointer-events-none absolute top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400" style={iconSide} />
                                    <Input
                                        value={companyName}
                                        onChange={(e) => setCompanyName(e.target.value)}
                                        className={inputPad}
                                        required
                                        data-testid="register-company-input"
                                    />
                                </div>
                            </div>

                            <div>
                                <Label className="mb-1.5 block text-sm font-medium">{t.emailLabel}</Label>
                                <div className="relative">
                                    <Mail className="pointer-events-none absolute top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400" style={iconSide} />
                                    <Input
                                        type="email"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        className={`${inputPad} ltr-text`}
                                        dir="ltr"
                                        placeholder="you@example.com"
                                        required
                                        data-testid="register-email-input"
                                    />
                                </div>
                                <p className="mt-1.5 text-xs text-neutral-500">{t.emailHint}</p>
                            </div>

                            <div>
                                <Label className="mb-1.5 block text-sm font-medium">{t.locationLabel}</Label>
                                <LocationPicker
                                    country={country}
                                    countryCode={countryCode}
                                    city={city}
                                    onChange={({ country: c, countryCode: cc, city: ct }) => {
                                        setCountry(c || "");
                                        setCountryCode(cc || "");
                                        setCity(ct || "");
                                    }}
                                    testIdPrefix="register-loc"
                                />
                            </div>

                            <div>
                                <Label className="mb-1.5 block text-sm font-medium">{t.passwordLabel}</Label>
                                <div className="relative">
                                    <Lock className="pointer-events-none absolute top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400" style={iconSide} />
                                    <Input
                                        type="password"
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        className={inputPad}
                                        required
                                        data-testid="register-password-input"
                                    />
                                </div>
                            </div>
                            <div>
                                <Label className="mb-1.5 block text-sm font-medium">{t.confirmPasswordLabel}</Label>
                                <Input
                                    type="password"
                                    value={confirm}
                                    onChange={(e) => setConfirm(e.target.value)}
                                    className={lang === "ar" ? "h-11 ps-3" : "h-11 pe-3"}
                                    required
                                    data-testid="register-confirm-input"
                                />
                            </div>

                            {error ? (
                                <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700" data-testid="register-error">
                                    {error}
                                </div>
                            ) : null}

                            <Button
                                type="submit"
                                disabled={loading}
                                className="h-11 w-full bg-neutral-900 text-white hover:bg-neutral-800"
                                data-testid="register-submit-btn"
                            >
                                {loading ? t.registerLoading : t.registerSubmit}
                            </Button>
                        </form>

                        <p className="mt-6 text-center text-sm text-neutral-600">
                            {t.registerHaveAccount}{" "}
                            <Link to="/login" className="font-semibold text-amber-700 hover:underline" data-testid="go-login-link">
                                {t.registerGoLogin}
                            </Link>
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
}

function ModeChoice({ active, onClick, title, testId }) {
    return (
        <button
            type="button"
            onClick={onClick}
            data-testid={testId}
            aria-pressed={active}
            className={`flex items-center justify-between gap-3 rounded-lg border px-3 py-2.5 text-start text-sm transition ${
                active
                    ? "border-amber-500 bg-amber-50 ring-2 ring-amber-200"
                    : "border-neutral-200 bg-white hover:border-amber-300 hover:bg-amber-50/50"
            }`}
        >
            <span className="font-medium text-neutral-800">{title}</span>
            <span
                className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2 ${
                    active ? "border-amber-600 bg-amber-600" : "border-neutral-300 bg-white"
                }`}
                aria-hidden
            >
                {active ? <span className="block h-2 w-2 rounded-full bg-white" /> : null}
            </span>
        </button>
    );
}
